import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import styles from "./ConfigurePage.module.css";
import ComponentCard from "../components/product/ComponentCard";
import StickyCartBar from "../components/product/StickyCartBar";
import { Badge, Breadcrumbs } from "../components/common";
import { useViewModel, useIsB2B } from "../hooks/useViewModel";
import { useCompatibilityValidation } from "../hooks/useCompatibilityValidation";
import { useAddToCart } from "../hooks/useAddToCart";
import { useBundlePrice } from "../hooks/useBundlePrice";
import { useProductSelection } from "../hooks/useProductSelection";
import type {
	ProductDto,
	ValidationStatus,
	RuleResult,
} from "../types/validation";
import { VALIDATION_STATUS } from "../types/validation";
import { InfoPanel } from "../components/common/InfoPanel.tsx";
import ArrowCirclePath from "../components/common/icons/ArrowCirclePath.tsx";
import { ArrowRight, Flex, Share } from "../components/common/icons";
import { openFlexInfoPopup } from "../services/flexInfoService.ts";
import RecommendationsList from "../components/RecommendationsList";
import { useRecommendations } from "../hooks/useRecommendations";
import { useUrlConfiguration } from "../hooks/useUrlConfiguration";
import { useInitialSelections } from "../hooks/useInitialSelections";
import { buildUnavailableWarningMessages } from "./configureWarningMessages";
import { buildAndEncodeConfiguration } from "../utils/urlSharing";
import Price from "../components/common/Price.tsx";
import { formatPrice, selectPrice } from "../utils/priceHelpers.ts";
import { customGaEvent } from "../utils/customGA";
import GamePerformanceWidget from "../components/games/GamePerformanceWidget";
import { useGamePerformance } from "../hooks/useGamePerformance";
import { useSelectedGames } from "../hooks/useSelectedGames";
import { usePopularGames } from "../hooks/usePopularGames";
import HelpDrawer from "../components/help/HelpDrawer";

const SUBGROUP_ID = "33-14";
const FLEX_RENEWAL_PERIOD = "24";

const ConfigurePage: React.FC = () => {
	useLayoutEffect(() => {
		window.IsConfigurePage = true;
		return () => {
			window.IsConfigurePage = false;
		};
	}, []);

	const { data, isLoading, error } = useViewModel();
	const isB2B = useIsB2B();
	const [cartError, setCartError] = useState<string | null>(null);
	const [shareSuccess, setShareSuccess] = useState<string | null>(null);

	const urlConfig = useUrlConfiguration(
		data?.preResolvedSelections,
		!!data,
		data?.viewModel?.id
	);

	const {
		initialSelections,
		isLoading: isInitialSelectionsLoading,
		allowStoredSelectionsFallback,
		replacedProductGroupIds,
		replacedGroupIdSet,
	} = useInitialSelections({
		configBundleId: data?.viewModel?.id || "",
		urlConfig,
	});

	const initialPrice = useMemo(() => {
		if (!data?.viewModel) return { net: 0, gross: 0 };

		return {
			net: data.viewModel.price.customerPriceNet,
			gross: data.viewModel.price.customerPrice,
		};
	}, [data?.viewModel]);

	const initialDiscount = useMemo(() => {
		if (!data?.viewModel) return { net: 0, gross: 0 };
		return {
			net: data.viewModel.price.discountNet,
			gross: data.viewModel.price.discount,
		};
	}, [data?.viewModel]);

	const { currentPrice, flexPrice, updatePrice } = useBundlePrice({
		configBundleId: data?.viewModel?.id || "",
		initialPrice,
		initialDiscount,
		taxRate: data?.viewModel?.price?.taxRate,
		subGroupId: SUBGROUP_ID,
		hasFlexAvailable: (data?.viewModel?.price?.subscriptionPrice || 0) > 0,
	});

	const { selectedComponents, handleProductSelect, handleReset, staleSelectionsCount, isInitialized } =
		useProductSelection({
			configBundleId: data?.viewModel?.id || "",
			initialPrice,
			currentPrice,
			productGroups: data?.viewModel?.productGroups,
			updatePrice,
			initialSelections: initialSelections,
			isUrlLoading: urlConfig.isLoading || isInitialSelectionsLoading,
			allowStoredSelectionsFallback,
		});

	const unavailableWarningMessages = useMemo(() => {
		if (!data?.strings) {
			return [];
		}

		return buildUnavailableWarningMessages({
			template: data.strings.product.configBundles.productsUnavailableWarning,
			hasUrlParam: urlConfig.hasUrlParam,
			missingProductsCount: urlConfig.missingProductsCount,
			replacedProductsCount: replacedProductGroupIds.length,
			staleSelectionsCount,
		});
	}, [
		data?.strings,
		urlConfig.hasUrlParam,
		urlConfig.missingProductsCount,
		replacedProductGroupIds.length,
		staleSelectionsCount,
	]);

	const prevEncodedConfigRef = useRef<string | null>(null);
	const urlUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (urlConfig.isInvalidConfig) {
			prevEncodedConfigRef.current = null;
			window.history.replaceState(null, "", window.location.pathname);
		}
	}, [urlConfig.isInvalidConfig]);

	useEffect(() => {
		if (!data?.viewModel?.productGroups || urlConfig.isLoading || urlConfig.isInvalidConfig) {
			return;
		}

		if (urlUpdateTimeoutRef.current) {
			clearTimeout(urlUpdateTimeoutRef.current);
		}

		urlUpdateTimeoutRef.current = setTimeout(() => {
			const config = buildAndEncodeConfiguration(selectedComponents, data.viewModel!.productGroups, data.viewModel!.id);
			const encoded = config?.encoded ?? null;

			if (encoded === prevEncodedConfigRef.current) {
				return;
			}

			prevEncodedConfigRef.current = encoded;
			const newUrl = config?.shareUrl ?? window.location.pathname;
			window.history.replaceState(null, "", newUrl);
		}, 300);

		return () => {
			if (urlUpdateTimeoutRef.current) {
				clearTimeout(urlUpdateTimeoutRef.current);
			}
		};
	}, [selectedComponents, data?.viewModel?.productGroups, urlConfig.isLoading, urlConfig.isInvalidConfig]);

	const selectedProducts = useMemo((): ProductDto[] => {
		if (!data?.viewModel) return [];

		return data.viewModel.productGroups
			.map((group) => {
				const selection = selectedComponents[group.id];
				const productId = selection?.productId ?? group.product?.id;

				if (!productId) return null;

				return {
					id: productId,
					componentTypeId: group.id,
					name: selection?.productName ?? group.product?.name,
				} as ProductDto;
			})
			.filter((p): p is ProductDto => p !== null);
	}, [selectedComponents, data?.viewModel]);

	const { data: validationData, isFetching: isValidating } =
		useCompatibilityValidation({
			configBundleId: data?.viewModel?.id || "",
			selectedProducts,
			enabled: !!data?.viewModel?.id && selectedProducts.length > 0,
		});

	const isIncompatible = !isValidating && validationData?.isValid === false;

	const compatibilityStatusCurrent = useMemo(() => {
		if (!validationData) return null;

		if (validationData.errors.length > 0) {
			return {
				type: VALIDATION_STATUS.ERROR,
				errors: validationData.errors,
			};
		}

		if (!validationData.isValid) {
			return {
				type: VALIDATION_STATUS.ERROR,
				message: data?.strings.product.configBundles.compatibility.notCompatible,
			};
		}

		if (validationData.warnings.length === 0) {
			return {
				type: VALIDATION_STATUS.SUCCESS,
				message: data?.strings.product.configBundles.compatibility.configurationCompatible,
			};
		}

		return null;
	}, [validationData, data?.strings]);

	const prevCompatibilityStatusRef = useRef(compatibilityStatusCurrent);
	const hasEverValidatedRef = useRef(false);
	if (!isValidating) {
		prevCompatibilityStatusRef.current = compatibilityStatusCurrent;
		if (validationData !== undefined) {
			hasEverValidatedRef.current = true;
		}
	}
	const compatibilityStatus = isValidating ? prevCompatibilityStatusRef.current : compatibilityStatusCurrent;
	const isAwaitingFirstValidation = !hasEverValidatedRef.current && isValidating;

	const isCustomized = useMemo(() => {
		return Object.keys(selectedComponents).length > 0;
	}, [selectedComponents]);

	const getComponentValidationStatus = (
		componentTypeId: string,
	): ValidationStatus | undefined => {
		if (!validationData || isValidating) return undefined;

		const componentProduct = selectedProducts.find(
			(p) => p.componentTypeId === componentTypeId,
		);
		if (!componentProduct) return undefined;

		const hasError = validationData.errors.some((error) =>
			error.relatedProducts?.includes(componentProduct.id),
		);

		if (hasError) return VALIDATION_STATUS.ERROR;

		const hasWarning = validationData.warnings.some((warning) =>
			warning.relatedProducts?.includes(componentProduct.id),
		);

		if (hasWarning) return VALIDATION_STATUS.WARNING;

		return VALIDATION_STATUS.SUCCESS;
	};

	const getComponentValidationErrors = (
		componentTypeId: string,
	): RuleResult[] => {
		if (!validationData || isValidating) return [];

		const componentProduct = selectedProducts.find(
			(p) => p.componentTypeId === componentTypeId,
		);
		if (!componentProduct) return [];

		const errors = validationData.errors
			.filter((error) => error.relatedProducts?.includes(componentProduct.id))
			.map((error) => ({ ...error, severity: VALIDATION_STATUS.ERROR }));

		const warnings = validationData.warnings
			.filter((warning) => warning.relatedProducts?.includes(componentProduct.id))
			.map((warning) => ({ ...warning, severity: VALIDATION_STATUS.WARNING }));

		return [...errors, ...warnings];
	};

	const {
		data: recommendations,
		isLoading: isLoadingRecommendations,
		error: recommendationsError,
	} = useRecommendations({
		bundleId: data?.viewModel?.id || "",
	});

	const productNames = useMemo(
		() => selectedProducts.map((p) => p.name).filter((n): n is string => !!n),
		[selectedProducts],
	);

	const { data: popularGames } = usePopularGames();
	const defaultFeaturedGames = useMemo(
		() => popularGames?.slice(0, 3).map((g) => g.title.toLowerCase()) ?? [],
		[popularGames],
	);
	const { gameTitles, updateGameTitles } = useSelectedGames({ featuredGames: defaultFeaturedGames });

	const handleGameTitlesChange = useCallback((newTitles: string[]) => {
		newTitles
			.filter((title) => !gameTitles.includes(title))
			.forEach((title) => {
				customGaEvent({
					event: "performance_calculator",
					app: "kpc_config",
					content_id: title,
				});
			});

		updateGameTitles(newTitles);
	}, [gameTitles, updateGameTitles]);

	const { data: gamePerformanceResult } = useGamePerformance({
		productNames,
		gameTitles,
		enabled: !!data?.viewModel && !urlConfig.isLoading && isInitialized,
	});

	const strings = data?.strings;

	const { addToCart, isAdding } = useAddToCart({
		configBundleId: data?.viewModel?.id || "",
		selectedComponents,
		productGroups: data?.viewModel?.productGroups ?? [],
		onSuccess: ({ renewalPeriod }) => {
			const vm = data?.viewModel;
			if (!vm) return;

			const isFlexPurchase = !!renewalPeriod;

			const categoriesObject = [...vm.breadCrumbs].reverse().reduce<Record<string, string>>(
				(acc, curr, index) => {
					acc[`item_category${index > 0 ? index + 1 : ""}`] = curr.title;
					return acc;
				},
				{},
			);

			customGaEvent({
				event: "add_to_cart",
				...(isFlexPurchase && { payment_type: "flex" }),
				...(isCustomized && { item_type: "configured" }),
				ecommerce: {
					items: [
						{
							item_id: vm.id,
							item_name: vm.name,
							price: currentPrice?.net ?? vm.price.customerPriceNet,
							item_leasable: isFlexPurchase,
							item_bundle: true,
							index: 1,
							quantity: 1,
							...categoriesObject,
						},
					],
				},
				_clear: true,
			});
		},
		onError: (error) => {
			setCartError(error);
			console.error("Failed to add to cart:", error);
		},
	});

	const handleAddToCart = useCallback(() => {
		setCartError(null);
		addToCart();
	}, [addToCart]);

	const handleFlexAddToCart = useCallback(() => {
		setCartError(null);
		addToCart({ renewalPeriod: FLEX_RENEWAL_PERIOD });
	}, [addToCart]);

	const handleOpenFlexInfoPopup = useCallback(() => {
		if (!data?.viewModel || !currentPrice) return;

		openFlexInfoPopup({
			productId: data.viewModel.id,
			price: currentPrice.net,
			taxRate: data.viewModel.price.taxRate,
			isConfigBundle: true,
			subGroup: SUBGROUP_ID,
			renewalPeriod: FLEX_RENEWAL_PERIOD,
		});
	}, [data?.viewModel, currentPrice]);

	const handleShareConfiguration = useCallback(async () => {
		if (!data?.viewModel) return;

		setShareSuccess(null);

		const vm = data.viewModel;

		const categoriesObject = [...vm.breadCrumbs].reverse().reduce<Record<string, string>>(
			(acc, curr, index) => {
				acc[`item_category${index > 0 ? index + 1 : ""}`] = curr.title;
				return acc;
			},
			{},
		);

		customGaEvent({
			app: "kpc_config",
			event: "share",
			content_type: "kpc",
			...(isCustomized && { item_type: "configured" }),
			items: [
				{
					item_id: vm.id,
					item_name: vm.name,
					price: currentPrice?.net ?? vm.price.customerPriceNet,
					item_bundle: true,
					index: 1,
					quantity: 1,
					...categoriesObject,
				},
			],
		});

		const config = buildAndEncodeConfiguration(selectedComponents, data.viewModel.productGroups, data.viewModel.id);

		const shareUrl = config?.shareUrl ?? window.location.origin + window.location.pathname;

		const configBundleStrings = strings?.product.configBundles;

		if (navigator.share) {
			try {
				await navigator.share({
					title: data.viewModel.name,
					url: shareUrl,
				});
				setShareSuccess(configBundleStrings?.shareSuccess || "");
			} catch (err) {
				if ((err as Error).name !== "AbortError") {
					console.error("Share failed:", err);
				}
			}
		} else {
			try {
				await navigator.clipboard.writeText(shareUrl);
				setShareSuccess(configBundleStrings?.linkCopied || "");
			} catch (err) {
				console.error("Clipboard copy failed:", err);
				setShareSuccess(configBundleStrings?.linkCopyFailed || "");
			}
		}
	}, [data, selectedComponents, strings]);

	if (isLoading || urlConfig.isLoading) {
		return <div>Loading...</div>;
	}

	if (error || !data) {
		return <div>Error loading configuration</div>;
	}

	if (!data.viewModel) {
		return <div>{data.notFoundMessage}</div>;
	}

	const { viewModel } = data;

	const mobilePrice = currentPrice
		? selectPrice(currentPrice, isB2B)
		: selectPrice({ net: viewModel.price.customerPriceNet, gross: viewModel.price.customerPrice }, isB2B);
	const mobileSavings = isB2B ? (viewModel.price.discountNet ?? 0) : (viewModel.price.discount ?? 0);
	const mobileListPrice = mobilePrice + mobileSavings;
	const mobileMonthly = flexPrice?.gross ?? viewModel.price.subscriptionPrice ?? 0;

	return (
		<>
			<Breadcrumbs
				breadCrumbs={[...viewModel.breadCrumbs].reverse()}
				productName={viewModel.name}
				productUrl={
					data.prettyUrl
						? `/product/${viewModel.id}/${data.prettyUrl}`
						: `/product/${viewModel.id}`
				}
				strings={{
					home: data.strings.product.configBundles.home,
					configure: data.strings.product.configBundles.configure,
				}}
			/>
			<main className={styles.main}>
				<div className={styles.container}>
					{/* Mobile-only title — shown above the grid on small screens */}
					<h1 className={styles.mobileTitle}>{viewModel.name}</h1>
					{viewModel.tag && viewModel.tag.title && (
						<div className={styles.mobileBadgeContainer}>
							<Badge color={viewModel.tag.color} url={viewModel.tag.url}>
								{viewModel.tag.title}
							</Badge>

							<span className={styles.badgeDescription}>
								{viewModel.tag.description}
							</span>
						</div>
					)}
					<div className={styles.grid}>
						<div className={styles.leftColumn}>
							<div className={styles.imageWrapper}>
								<img
									src={`/img/p/800/${viewModel.id}.jpg`}
									srcSet={`/img/p/400/${viewModel.id}.jpg 400w, /img/p/600/${viewModel.id}.jpg 600w, /img/p/800/${viewModel.id}.jpg 800w /img/p/1200/${viewModel.id}.jpg 1200w`}
									sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 800px"
									width={800}
									height={800}
									alt={viewModel.name}
									className={styles.productImage}
									fetchPriority="high"
								/>
							<button
								className={styles.imageShareButton}
								onClick={handleShareConfiguration}
								aria-label={strings?.product.configBundles.shareConfiguration}
								data-testid="image-share-configuration-button"
							>
								<Share aria-hidden="true" />
								</button>
							</div>
							{/* Mobile-only: price + action buttons shown under the image */}
							<div className={styles.mobileActionsSection}>
								<Price
									price={mobilePrice}
									savings={mobileSavings}
									listPrice={mobileListPrice}
									size="large"
								/>
								<button
									className={`${styles.mobileCtaButton} btn btn-large primary`}
									onClick={handleAddToCart}
									disabled={isAdding || isIncompatible}
									data-testid="mobile-add-to-cart-button"
								>
									{strings?.product.configBundles.buyButton}
								</button>
								{mobileMonthly > 0 && (
									<>
										<button
											className={styles.mobileFlexButton}
											onClick={handleFlexAddToCart}
											disabled={isAdding || isIncompatible}
											data-testid="mobile-flex-add-to-cart-button"
										>
											<span
												dangerouslySetInnerHTML={{
													__html:
														strings?.product.configBundles.subscriptionBuyButton.replace(
															"{monthlyPrice}",
															` ${String(formatPrice(mobileMonthly, "nb-NO"))},-`,
														) ?? "",
												}}
											></span>
										</button>
									<button
										className={styles.mobileFlexInfoButton}
										onClick={handleOpenFlexInfoPopup}
										disabled={isAdding}
										data-testid="mobile-flex-information-button"
									>
										<span>
											{strings?.product?.configBundles?.shortAbout}
										</span>
										<Flex aria-hidden="true" />
										<ArrowRight aria-hidden="true" />
										</button>
									</>
								)}
							</div>

						<div className={styles.gameWidgetContainer}>
						{gamePerformanceResult && (
							<GamePerformanceWidget
								result={gamePerformanceResult}
								selectedGames={gameTitles}
								onGameTitlesChange={handleGameTitlesChange}
							/>
						)}
						</div>
						</div>

						<div className={styles.rightColumn}>
							<div className={styles.productInfo}>
								{viewModel.tag && viewModel.tag.title && (
									<div className={styles.badgeContainer}>
										<Badge color={viewModel.tag.color} url={viewModel.tag.url}>
											{viewModel.tag.title}
										</Badge>

										<span className={styles.badgeDescription}>
											{viewModel.tag.description}
										</span>
									</div>
								)}

								<h1 className={styles.title}>{viewModel.name}</h1>

								<div className={styles.compatibilityInfoPanels}>
									{cartError && (
										<InfoPanel type="error" message={cartError} />
									)}
									{urlConfig.isInvalidConfig &&
										!!urlConfig.invalidConfigMessage && (
											<InfoPanel
												type="warning"
												message={urlConfig.invalidConfigMessage}
											/>
										)}
									{unavailableWarningMessages.map((message, index) => (
										<InfoPanel
											key={`unavailable-warning-${index}`}
											type="warning"
											message={message}
										/>
									))}
									{shareSuccess && (
										<InfoPanel type="success" message={shareSuccess} />
									)}
									{isAwaitingFirstValidation && !cartError && !shareSuccess && unavailableWarningMessages.length === 0 && (
										<div style={{ visibility: "hidden" }}>
											<InfoPanel type="success" message="" />
										</div>
									)}
									{compatibilityStatus && (
										<>
											{compatibilityStatus.type === VALIDATION_STATUS.ERROR &&
											"errors" in compatibilityStatus ? (
												compatibilityStatus?.errors?.map((error, index) => (
													<InfoPanel
														key={index}
														type="error"
														message={error.message}
													/>
												))
											) : (
												<InfoPanel
													type={
														compatibilityStatus.type.toLowerCase() as
															| "success"
															| "error"
															| "warning"
													}
													message={
														"message" in compatibilityStatus
															? compatibilityStatus.message
															: undefined
													}
												/>
											)}
										</>
									)}
								</div>

								<div className={styles.resetConfigurationButtonContainer} style={{ visibility: isCustomized ? "visible" : "hidden" }}>
								<button
									className={styles.resetConfigurationButton}
									onClick={handleReset}
									data-testid="reset-configuration-button"
									tabIndex={isCustomized ? 0 : -1}
								>
									<span className={styles.flexButtonText}>
										{data?.strings.product.configBundles.reset}
									</span>
									<ArrowCirclePath aria-hidden="true" />
									</button>
								</div>

								<div className={styles.componentsList}>
								<h2 className="sr-only">{data.strings.product.configBundles.configure}</h2>
									{viewModel.productGroups.map((group) => {
										const selection = selectedComponents[group.id];
										const defaultProduct = group.product ?? null;
										const selectionHasProduct = !!selection?.productId;
										const userClearedSelection = selection?.productId === null;

										const displayProduct = selectionHasProduct
											? {
													id: selection!.productId!,
													name: selection?.productName,
													description: selection?.productDescription,
													description2: selection?.productDescription2,
													imageUrl: selection?.imageUrl,
													productInfo: {},
													availabilityQuantity:
														selection?.availabilityQuantity ?? 0,
													availabilityStatus:
														selection?.availabilityStatus || "",
													calculatedStockQuantity:
														selection?.calculatedStockQuantity ?? 0,
													stockStatus: selection?.stockStatus,
												}
											: userClearedSelection
												? null
												: defaultProduct;

										const isEmpty =
											userClearedSelection || (!selection && !defaultProduct);
										const imageUrl =
											displayProduct?.imageUrl ||
											(displayProduct?.id
												? `https://www.komplett.no/img/p/200/${displayProduct.id}.jpg`
												: undefined);
										const selectedProductId = selection
											? selection.productId
											: (defaultProduct?.id ?? null);
										const isCustomized = selection
											? defaultProduct
												? selection.productId !== defaultProduct.id
												: selection.productId !== null
											: false;

										const isAlwaysIncluded =
											group.selectionType === "AlwaysIncluded";

										const isReplaced = replacedGroupIdSet.has(group.id.trim().toLowerCase());

										return (
											<ComponentCard
												key={group.product?.id ? `${group.id}_${group.product.id}` : group.id}
												kind={group.label || group.name}
												title={displayProduct?.name}
												performanceComponentType={group.performanceComponentType ?? null}
												meta={
													displayProduct?.productInfo
														? Object.values(displayProduct.productInfo)
														: undefined
												}
												description={displayProduct?.description}
												imageUrl={imageUrl}
												configBundleId={viewModel.id}
												productGroupId={group.id}
												materialNumber={displayProduct?.id}
												onProductSelect={handleProductSelect}
												selectedProductId={selectedProductId}
												defaultProductName={defaultProduct?.name ?? null}
												stockStatus={displayProduct?.stockStatus}
												isCustomized={isCustomized}
												forcedDefault={group.forcedDefault || isAlwaysIncluded}
												isEmpty={isEmpty}
												canSelectEmpty={
													!group.requiredSelection && !isAlwaysIncluded
												}
												currentConfiguration={selectedProducts}
												validationStatus={getComponentValidationStatus(
													group.id,
												)}
												isReplaced={isReplaced}
												validationErrors={getComponentValidationErrors(
													group.id,
												)}
											/>
										);
									})}
								</div>

								<RecommendationsList
									recommendations={recommendations}
									isLoading={isLoadingRecommendations}
									error={recommendationsError}
								/>
							</div>
						</div>
					</div>
				</div>
			</main>

			<StickyCartBar
				onAddToCart={handleAddToCart}
				onFlexAddToCart={handleFlexAddToCart}
				onOpenFlexPopup={handleOpenFlexInfoPopup}
				onShareConfiguration={handleShareConfiguration}
				isLoading={isAdding}
				isIncompatible={isIncompatible}
				currentPrice={currentPrice}
				flexPrice={flexPrice}
			/>

			<HelpDrawer />
		</>
	);
};

export default ConfigurePage;